/**
 * WebGPU PDF Chat - Main Application
 * Orchestrates LLM engine, PDF service, TTS/STT, and RAG with embeddings
 */
import './style.css';
import { llmEngine } from './llm-engine.js';
import { documentService } from './document-service.js';
import { ttsService } from './tts-service.js';
import { sttService } from './stt-service.js';
import { embeddingService } from './embedding-service.js';
import { vectorStore } from './vector-store.js';
import ChatUI from './chat-ui.js';

// DOM Elements
const webgpuWarning = document.getElementById('webgpu-warning');
const modelStatus = document.getElementById('model-status');
const statusText = modelStatus.querySelector('.status-text');

const pdfDropZone = document.getElementById('pdf-drop-zone');
const pdfInput = document.getElementById('pdf-input');
const pdfInfo = document.getElementById('pdf-info');
const pdfName = document.getElementById('pdf-name');
const pdfPages = document.getElementById('pdf-pages');
const btnRemovePdf = document.getElementById('btn-remove-pdf');
const extractionStatus = document.getElementById('extraction-status');
const extractionBar = document.getElementById('extraction-bar');
const extractionText = document.getElementById('extraction-text');

const modelSelect = document.getElementById('model-select');
const btnLoadModel = document.getElementById('btn-load-model');
const loadingProgress = document.getElementById('loading-progress');
const progressFill = document.getElementById('progress-fill');
const progressText = document.getElementById('progress-text');

// Initialize Chat UI
const chatUI = new ChatUI(document.getElementById('chat-messages'));

// TTS/STT state
let ttsInitialized = false;
let sttInitialized = false;
let autoSpeakEnabled = true;

// Mic button and language
const btnMic = document.getElementById('btn-mic');
const chatInput = document.getElementById('chat-input');
const languageSelect = document.getElementById('language-select');

// Get selected language
function getSelectedLanguage() {
  return languageSelect.value || 'fr';
}

/**
 * Initialize the application
 */
async function init() {
  const hasWebGPU = await llmEngine.constructor.checkWebGPU();

  if (!hasWebGPU) {
    webgpuWarning.classList.remove('hidden');
    btnLoadModel.disabled = true;
    setStatus('error', 'WebGPU non disponible');
    return;
  }

  setStatus('idle', 'Prêt à charger');

  setupDocumentHandlers();
  setupModelHandlers();
  setupChatHandlers();
  setupTTSHandlers();
  setupSTTHandlers();

  // Initialize TTS in background
  initTTSBackground();
}

/**
 * Initialize TTS in background
 */
async function initTTSBackground() {
  try {
    console.log('Initializing TTS in background...');
    await ttsService.init((progress) => {
      console.log('TTS:', progress.text);
    });
    ttsInitialized = true;
    console.log('TTS ready!');
  } catch (error) {
    console.warn('TTS init failed:', error.message);
  }
}

/**
 * Clean text for TTS (remove markdown, emojis, etc.)
 */
function cleanTextForTTS(text) {
  return text
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/`(.*?)`/g, '$1')
    .replace(/[\u{1F300}-\u{1F9FF}]/gu, '')
    .replace(/\n+/g, '. ')
    .trim();
}

/**
 * Auto-speak text using selected language
 */
async function autoSpeak(text) {
  if (!autoSpeakEnabled || !ttsInitialized) return;

  try {
    const cleanText = cleanTextForTTS(text);
    const lang = getSelectedLanguage();
    if (cleanText.length > 0) {
      await ttsService.speak(cleanText, lang);
    }
  } catch (error) {
    console.error('Auto-speak error:', error);
  }
}

/**
 * Setup Document upload handlers
 */
function setupDocumentHandlers() {
  pdfDropZone.addEventListener('click', () => pdfInput.click());

  pdfInput.addEventListener('change', (e) => {
    if (e.target.files[0]) {
      handleDocumentUpload(e.target.files[0]);
    }
  });

  pdfDropZone.addEventListener('dragover', (e) => {
    e.preventDefault();
    pdfDropZone.classList.add('dragover');
  });

  pdfDropZone.addEventListener('dragleave', () => {
    pdfDropZone.classList.remove('dragover');
  });

  pdfDropZone.addEventListener('drop', (e) => {
    e.preventDefault();
    pdfDropZone.classList.remove('dragover');

    const file = e.dataTransfer.files[0];
    if (file && (file.type === 'application/pdf' || file.type === 'text/plain' || file.name.endsWith('.txt') || file.name.endsWith('.pdf'))) {
      handleDocumentUpload(file);
    }
  });

  btnRemovePdf.addEventListener('click', () => {
    documentService.clear();
    vectorStore.clear(); // Clear RAG embeddings
    pdfDropZone.classList.remove('hidden');
    pdfInfo.classList.add('hidden');
    extractionStatus.classList.add('hidden');
    chatUI.showContextBadge(false);
    pdfInput.value = '';
  });
}

/**
 * Handle Document file upload with RAG embeddings
 */
async function handleDocumentUpload(file) {
  pdfDropZone.classList.add('hidden');
  extractionStatus.classList.remove('hidden');
  extractionBar.style.width = '0%';
  extractionText.textContent = 'Extraction du texte...';

  try {
    // Step 1: Extract text from Document
    await documentService.extractText(file, (progress, pageNum) => {
      extractionBar.style.width = `${progress * 0.4}%`; // 0-40%
      if (documentService.fileType === 'pdf') {
        extractionText.textContent = `Page ${pageNum}/${documentService.pageCount}...`;
      } else {
        extractionText.textContent = `Lecture du fichier... ${progress}%`;
      }
    });

    // Step 2: Initialize embedding service if needed
    extractionText.textContent = 'Chargement embeddings...';
    extractionBar.style.width = '45%';

    if (!embeddingService.isReady) {
      await embeddingService.init((progress) => {
        extractionBar.style.width = `${45 + progress.progress * 0.15}%`; // 45-60%
      });
    }

    // Step 3: Chunk and embed the text
    extractionText.textContent = 'Création des embeddings...';
    const chunks = documentService.getChunks(500, 50);

    const embeddings = await embeddingService.embedBatch(chunks, (progress) => {
      const pct = 60 + (progress.current / progress.total) * 35; // 60-95%
      extractionBar.style.width = `${pct}%`;
      extractionText.textContent = `Embedding ${progress.current}/${progress.total}...`;
    });

    // Step 4: Store in vector store
    const items = chunks.map((text, i) => ({ text, embedding: embeddings[i] }));
    vectorStore.addChunks(items, file.name);

    extractionBar.style.width = '100%';
    extractionStatus.classList.add('hidden');
    pdfInfo.classList.remove('hidden');
    pdfName.textContent = file.name;

    if (documentService.fileType === 'pdf') {
      pdfPages.textContent = `${documentService.pageCount} pages, ${chunks.length} chunks`;
    } else {
      pdfPages.textContent = `Texte, ${chunks.length} chunks`;
    }

    chatUI.showContextBadge(true);

    const msg = `**${file.name}** indexé ! (${chunks.length} chunks)\n\n🔍 **RAG activé** - Recherche sémantique sur le document.`;
    chatUI.addMessage('assistant', msg, false);

  } catch (error) {
    console.error('Document upload error:', error);
    extractionStatus.classList.add('hidden');
    pdfDropZone.classList.remove('hidden');
    chatUI.addErrorMessage(`Impossible de traiter le document: ${error.message}`);
  }
}

/**
 * Setup model loading handlers
 */
function setupModelHandlers() {
  btnLoadModel.addEventListener('click', async () => {
    const modelId = modelSelect.value;

    btnLoadModel.disabled = true;
    modelSelect.disabled = true;
    loadingProgress.classList.remove('hidden');
    setStatus('loading', 'Chargement...');

    try {
      await llmEngine.init(modelId, (progress) => {
        progressFill.style.width = `${progress.progress}%`;
        progressText.textContent = progress.text;
      });

      setStatus('ready', modelId.split('-')[0]);
      chatUI.setInputEnabled(true);

      setTimeout(() => {
        loadingProgress.classList.add('hidden');
      }, 1000);

      const msg = `Modèle **${modelId.split('-')[0]}** prêt ! Posez vos questions.`;
      chatUI.addMessage('assistant', msg, false);

    } catch (error) {
      console.error('Model loading error:', error);
      setStatus('error', 'Erreur');
      chatUI.addErrorMessage(`Erreur: ${error.message}`);
      loadingProgress.classList.add('hidden');
    }

    btnLoadModel.disabled = false;
    modelSelect.disabled = false;
  });
}

/**
 * Setup chat message handlers
 */
function setupChatHandlers() {
  chatUI.onSendMessage = async (text) => {
    if (!llmEngine.isReady) {
      chatUI.addErrorMessage('Veuillez d\'abord charger un modèle.');
      return;
    }

    const messages = await buildMessagesWithContext(text);
    chatUI.startStreamingMessage();

    try {
      const response = await llmEngine.chat(messages, (delta, fullText) => {
        chatUI.updateStreamingMessage(fullText);
      });

      chatUI.finishStreamingMessage(response);

      // AUTO-SPEAK: Speak the response automatically!
      autoSpeak(response);

    } catch (error) {
      console.error('Chat error:', error);
      chatUI.addErrorMessage(error.message);
    }
  };
}

/**
 * Setup TTS handlers (for manual speak button)
 */
function setupTTSHandlers() {
  chatUI.onSpeakMessage = async (text, onComplete) => {
    if (text === null) {
      ttsService.stop();
      return;
    }

    if (!ttsInitialized) {
      try {
        await ttsService.init();
        ttsInitialized = true;
      } catch (error) {
        console.error('TTS init failed:', error);
        if (onComplete) onComplete();
        return;
      }
    }

    try {
      const cleanText = cleanTextForTTS(text);
      await ttsService.speak(cleanText);
      if (onComplete) onComplete();
    } catch (error) {
      console.error('TTS error:', error);
      if (onComplete) onComplete();
    }
  };
}

/**
 * Build messages array with RAG context
 * Uses semantic search to find relevant chunks
 */
async function buildMessagesWithContext(userMessage) {
  const messages = [];

  let systemPrompt = 'Tu es un assistant IA serviable et amical. Réponds en français de manière concise et précise.';

  // Use RAG if we have indexed documents
  if (vectorStore.hasDocuments() && embeddingService.isReady) {
    try {
      // Embed the user query
      const queryEmbedding = await embeddingService.embed(userMessage);

      // Search for relevant chunks
      const results = vectorStore.search(queryEmbedding, 5);

      if (results.length > 0) {
        const relevantContext = results
          .map((r, i) => `[Extrait ${i + 1}] ${r.text}`)
          .join('\n\n');

        console.log(`RAG: Found ${results.length} relevant chunks (scores: ${results.map(r => r.score.toFixed(2)).join(', ')})`);

        systemPrompt += `\n\nContexte du document (extraits pertinents):\n---\n${relevantContext}\n---\n\nUtilise ces extraits pour répondre à la question. Si l'information n'est pas dans les extraits, dis-le.`;
      }
    } catch (error) {
      console.error('RAG search error:', error);
      // Fallback to simple context
      if (documentService.hasDocument()) {
        const docContext = documentService.getContextSummary(4000);
        systemPrompt += `\n\nContenu du document:\n---\n${docContext}\n---`;
      }
    }
  } else if (documentService.hasDocument()) {
    // Fallback if embeddings not ready
    const docContext = documentService.getContextSummary(4000);
    systemPrompt += `\n\nContenu du document:\n---\n${docContext}\n---\n\nRéponds aux questions sur ce document.`;
  }

  messages.push({ role: 'system', content: systemPrompt });

  const history = chatUI.getMessagesForContext().slice(-10);
  messages.push(...history);
  messages.push({ role: 'user', content: userMessage });

  return messages;
}

/**
 * Set model status indicator
 */
function setStatus(status, text) {
  modelStatus.className = 'model-status';
  if (status !== 'idle') {
    modelStatus.classList.add(status);
  }
  statusText.textContent = text;
}

/**
 * Setup STT (speech-to-text) handlers
 */
function setupSTTHandlers() {
  let isRecording = false;

  // Enable mic when model is loaded
  const originalSetInputEnabled = chatUI.setInputEnabled.bind(chatUI);
  chatUI.setInputEnabled = (enabled) => {
    originalSetInputEnabled(enabled);
    btnMic.disabled = !enabled;
  };

  // Mouse/touch events for push-to-talk
  btnMic.addEventListener('mousedown', startRecording);
  btnMic.addEventListener('mouseup', stopRecording);
  btnMic.addEventListener('mouseleave', stopRecording);
  btnMic.addEventListener('touchstart', (e) => { e.preventDefault(); startRecording(); });
  btnMic.addEventListener('touchend', stopRecording);

  async function startRecording() {
    if (isRecording || btnMic.disabled) return;
    isRecording = true;
    btnMic.classList.add('recording');

    // Initialize STT if needed
    if (!sttInitialized) {
      try {
        console.log('Initializing STT...');
        chatInput.placeholder = 'Chargement Whisper...';
        await sttService.init((progress) => {
          console.log('STT:', progress.text);
        });
        sttInitialized = true;
        console.log('STT ready!');
      } catch (error) {
        console.error('STT init failed:', error);
        chatInput.placeholder = 'Erreur micro';
        btnMic.classList.remove('recording');
        isRecording = false;
        return;
      }
    }

    try {
      chatInput.placeholder = '🎤 Parlez...';
      await sttService.startRecording();
    } catch (error) {
      console.error('Recording start failed:', error);
      chatInput.placeholder = 'Erreur micro: ' + error.message;
      btnMic.classList.remove('recording');
      isRecording = false;
    }
  }

  async function stopRecording() {
    if (!isRecording) return;
    isRecording = false;
    btnMic.classList.remove('recording');
    chatInput.placeholder = 'Transcription...';

    try {
      const lang = getSelectedLanguage();
      const text = await sttService.stopRecording(lang);
      chatInput.placeholder = 'Tapez votre message...';

      if (text && text.trim()) {
        const cleanText = text.trim();

        // Auto-send the transcribed message
        if (chatUI.onSendMessage && llmEngine.isReady) {
          chatUI.addMessage('user', cleanText);
          chatUI.onSendMessage(cleanText);
        } else {
          // Fallback: put in input if model not ready
          chatInput.value = cleanText;
          chatInput.focus();
        }
      }
    } catch (error) {
      console.error('Transcription failed:', error);
      chatInput.placeholder = 'Tapez votre message...';
    }
  }
}

// Initialize app
init();
